import type {
  DesktopControlApprovalPort,
  DesktopControlNoticeIntl,
  PendingToolApproval,
  ToolApprovalVerdict,
} from '@heidi/agents/contracts';
import {
  acquireDesktopControlLease,
  createDesktopControlApprovalReservation,
  DESKTOP_CONTROL_BUSY_TOOL_RESULT,
  getDesktopControlBlocker,
  getDesktopControlBlockerForLease,
  getDesktopControlBusyApprovalMessage,
  isComputerUseApprovalCandidate,
  releaseDesktopControlApprovalReservation,
  unwrapAcquireResult,
  useDesktopControlBlocker,
  useDesktopControlLease,
  type DesktopControlReadParams,
} from '@heidi/agents/data';
import { toast } from '@heidi/design-system/toast';
import type { DesktopControlLease, DesktopControlOwner } from '@heidi/plugin-rpa';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { readApprovalLabelFromToolInput } from '../tool-approvals/desktop-approval-blockers';

export interface DesktopControlledApproval {
  approvalId: string;
  agentRunId?: string | null;
  toolCallId?: string | null;
  toolName?: string | null;
  approvalKind?: string | null;
  toolInput?: Record<string, unknown> | null;
}

interface ResolveDesktopControlledApprovalArgs {
  approval: DesktopControlledApproval;
  verdict: ToolApprovalVerdict;
  owner?: Partial<DesktopControlOwner>;
  resolve: () => Promise<unknown>;
  onBlocked?: (blocker: DesktopControlLease) => void;
}

export interface ResolveDesktopControlledApprovalResult {
  resolved: boolean;
  blocker?: DesktopControlLease;
  leaseId?: string;
}

export const isDesktopControlledApproval = (
  approval: DesktopControlledApproval
): boolean =>
  isComputerUseApprovalCandidate({
    toolName: approval.toolName,
    approvalKind: approval.approvalKind,
  });

export const desktopControlReadParamsForApproval = (
  approval: DesktopControlledApproval
): DesktopControlReadParams | null => {
  if (!approval.toolCallId) return null;
  return {
    ownerKind: 'project_delegate',
    ownerId: approval.toolCallId,
    agentRunId: approval.agentRunId ?? null,
  };
};

export const getDesktopControlApprovalBlockerForLease = (
  current: DesktopControlLease | null | undefined,
  approval: DesktopControlledApproval
): DesktopControlLease | null => {
  if (!isDesktopControlledApproval(approval)) return null;
  return getDesktopControlBlockerForLease(
    current,
    desktopControlReadParamsForApproval(approval)
  );
};

export const useDesktopControlApprovalBlocker = (
  approval: DesktopControlledApproval | null | undefined
): DesktopControlLease | null => {
  const isDesktopControlled =
    approval !== null && approval !== undefined && isDesktopControlledApproval(approval);
  return useDesktopControlBlocker(
    isDesktopControlled ? desktopControlReadParamsForApproval(approval) : null
  );
};

export const showDesktopControlApprovalBlockedToast = (
  intl: DesktopControlNoticeIntl,
  blocker: DesktopControlLease
): void => {
  toast({
    tone: 'warning',
    title: intl.formatMessage({
      id: '58mGXYkvyF',
      defaultMessage: 'Desktop task already running',
    }),
    description: getDesktopControlBusyApprovalMessage(blocker),
  });
};

export const pendingApprovalToDesktopControlledApproval = (
  approval: PendingToolApproval
): DesktopControlledApproval => ({
  approvalId: approval.approvalId,
  agentRunId: approval.agentRunId,
  toolCallId: approval.toolCallId,
  toolName: approval.toolName,
  approvalKind: approval.approvalKind,
  toolInput: approval.toolInput,
});

const wasResolveAccepted = (response: unknown): boolean => {
  if (response === null || typeof response !== 'object') return true;
  if (!('accepted' in response)) return true;
  return (response as { accepted?: unknown }).accepted !== false;
};

export const resolveDesktopControlledApproval = async ({
  approval,
  verdict,
  owner,
  resolve,
  onBlocked,
}: ResolveDesktopControlledApprovalArgs): Promise<ResolveDesktopControlledApprovalResult> => {
  if (
    verdict !== 'approve' ||
    !approval.toolCallId ||
    !isDesktopControlledApproval(approval)
  ) {
    const response = await resolve();
    return { resolved: wasResolveAccepted(response) };
  }

  const blocker = getDesktopControlBlocker(desktopControlReadParamsForApproval(approval));
  if (blocker) {
    onBlocked?.(blocker);
    return { resolved: false, blocker };
  }

  const label = owner?.label ?? readDesktopControlledApprovalLabel(approval);
  const leaseOwner: DesktopControlOwner = {
    ownerKind: 'project_delegate',
    ownerId: approval.toolCallId,
    agentRunId: approval.agentRunId ?? undefined,
    toolCallId: approval.toolCallId,
    ...owner,
    label,
  };
  const acquired = unwrapAcquireResult(await acquireDesktopControlLease(leaseOwner));
  if (!acquired.ok) {
    onBlocked?.(acquired.blocker);
    return { resolved: false, blocker: acquired.blocker };
  }
  createDesktopControlApprovalReservation({
    approvalId: approval.approvalId,
    toolCallId: approval.toolCallId,
    lease: acquired.lease,
    owner: leaseOwner,
  });

  try {
    const response = await resolve();
    if (!wasResolveAccepted(response)) {
      await releaseDesktopControlApprovalReservation(approval.toolCallId, {
        approval_id: approval.approvalId,
        tool_call_id: approval.toolCallId,
      });
      return { resolved: false };
    }
  } catch (error) {
    void releaseDesktopControlApprovalReservation(approval.toolCallId, {
      approval_id: approval.approvalId,
      tool_call_id: approval.toolCallId,
    });
    throw error;
  }

  return { resolved: true, leaseId: acquired.lease.leaseId };
};

const readDesktopControlledApprovalLabel = (
  approval: DesktopControlledApproval
): string | undefined => readApprovalLabelFromToolInput(approval.toolInput);

/**
 * The approval cards' port onto this lease, bound once so every composition point
 * hands the cards the same object. The plain members are the computer-use
 * renderer's: it renders outside a hook, over the lease snapshot its caller holds.
 */
export const desktopControlApprovalPort: DesktopControlApprovalPort = {
  useBlockedMessage: (approval) => {
    const blocker = useDesktopControlApprovalBlocker(approval);
    return approval && blocker ? getDesktopControlBusyApprovalMessage(blocker) : null;
  },
  useResolve: () => {
    const intl = useIntl();
    return useCallback(
      (args) =>
        resolveDesktopControlledApproval({
          ...args,
          onBlocked: (blocker) => showDesktopControlApprovalBlockedToast(intl, blocker),
        }),
      [intl]
    );
  },
  useLease: () => useDesktopControlLease(),
  readBlockedMessage: (lease, approval) => {
    const blocker = getDesktopControlApprovalBlockerForLease(lease, approval);
    return blocker ? getDesktopControlBusyApprovalMessage(blocker) : null;
  },
  resolve: (args, intl) =>
    resolveDesktopControlledApproval({
      ...args,
      onBlocked: (blocker) => showDesktopControlApprovalBlockedToast(intl, blocker),
    }),
  describeBusyToolResult: (toolResult) =>
    toolResult === DESKTOP_CONTROL_BUSY_TOOL_RESULT
      ? getDesktopControlBusyApprovalMessage(null)
      : null,
};
